import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAnalyticsScorecard, type ScorecardData } from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface TrendIndicatorProps {
  percent: number;
  suffix?: string;
}

function TrendIndicator({ percent, suffix = "" }: TrendIndicatorProps) {
  const isPositive = percent > 0;
  const isNegative = percent < 0;
  const isNeutral = percent === 0;

  return (
    <div
      className={cn(
        "flex items-center gap-1 text-sm",
        isPositive && "text-green-600",
        isNegative && "text-red-600",
        isNeutral && "text-muted-foreground"
      )}
    >
      {isPositive && <TrendingUp className="h-4 w-4" />}
      {isNegative && <TrendingDown className="h-4 w-4" />}
      {isNeutral && <Minus className="h-4 w-4" />}
      <span>
        {isPositive && "+"}
        {percent.toFixed(1)}%{suffix && ` ${suffix}`}
      </span>
    </div>
  );
}

interface ScorecardItemProps {
  title: string;
  value: string | number;
  vsYesterday: { value: number; percent: number };
  vsAverage: { value: number; percent: number };
  loading?: boolean;
}

function ScorecardItem({
  title,
  value,
  vsYesterday,
  vsAverage,
  loading,
}: ScorecardItemProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24 mb-3" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-28 mt-1" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold mb-3">{value}</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>vs yesterday:</span>
            <TrendIndicator percent={vsYesterday.percent} />
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>vs 30d avg:</span>
            <TrendIndicator percent={vsAverage.percent} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TasksCard({
  data,
  loading,
}: {
  data?: ScorecardData;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Tasks Today
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-24 mb-3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full mt-1" />
        </CardContent>
      </Card>
    );
  }

  const tasks = data?.tasks;
  if (!tasks) return null;

  const successRateDiff = tasks.vsAverageSuccessRate;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Tasks Today
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold mb-3">
          {tasks.completed}/{tasks.total}
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Completed</span>
            <span className="text-green-600">{tasks.completed}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">In Progress</span>
            <span className="text-blue-600">{tasks.inProgress}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Failed</span>
            <span className="text-red-600">{tasks.failed}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Pending</span>
            <span>{tasks.pending}</span>
          </div>
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">Success Rate</span>
              <div className="flex items-center gap-1">
                <span className="font-medium">
                  {tasks.successRate.toFixed(1)}%
                </span>
                <TrendIndicator percent={successRateDiff} />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export default function ScorecardRow() {
  const { data, isLoading, error } = useAnalyticsScorecard();

  if (error) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="col-span-full">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              Failed to load scorecard data
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const scorecard = data;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <ScorecardItem
        title="Orders Today"
        value={scorecard?.orders?.count ?? 0}
        vsYesterday={scorecard?.orders?.vsYesterday ?? { value: 0, percent: 0 }}
        vsAverage={scorecard?.orders?.vsAverage ?? { value: 0, percent: 0 }}
        loading={isLoading}
      />
      <TasksCard data={scorecard} loading={isLoading} />
      <ScorecardItem
        title="Revenue Today"
        value={formatCurrency(scorecard?.revenue?.cents ?? 0)}
        vsYesterday={{
          value: scorecard?.revenue?.vsYesterday?.cents ?? 0,
          percent: scorecard?.revenue?.vsYesterday?.percent ?? 0,
        }}
        vsAverage={{
          value: scorecard?.revenue?.vsAverage?.cents ?? 0,
          percent: scorecard?.revenue?.vsAverage?.percent ?? 0,
        }}
        loading={isLoading}
      />
      {isLoading ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Skeleton className="h-8 w-32" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Scorecard For
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {scorecard?.date
                ? new Date(scorecard.date).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                : "Today"}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Compared against yesterday and 30-day rolling average
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
